import React, { useState, useCallback, useEffect } from "react";
import "./DynamicFilters.css";
import "../FlightsFilters.css";
import StringInput from "../../../Inputs/StringInput";
import CheckboxInput from "../../../Inputs/CheckboxInput";
import InputX from "../../../Inputs/InputX";
import { Button } from "../../../buttons/Buttons";
import { DatePicker } from "antd";
import Select from "../../../Inputs/Select";
import { AutoComplete } from "antd";
import { useTranslation } from "react-i18next";
import {
  getDynamicHotelNames,
  getDynamicAgentEmails,
} from "../../../../services/dynamicService";
import { isAdminUser } from "../../../../services/authService";
import { dateHebLocale } from "../../../Agent/SearchBars/OrganizedTourSearchBar/organizedTourSearchBar";

const filterInitialState = {
  order_dates: { start: "", end: "" },
  staying_dates: { start: "", end: "" },
  hotel_name: "",
  agent_email: "",
  order_id: "",
  status: "",
  paid: "", // For dropdown
  pending: false, // For checkbox
  cancelled: false, // For checkbox
  min_price: "",
  max_price: "",
  flight_booked: false, // For checkbox
  hotel_booked: false, // For checkbox
};

const DynamicFilters = ({ onFilterChange, isLoading }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(filterInitialState);
  const [hotelNames, setHotelNames] = useState([]);
  const [agentEmails, setAgentEmails] = useState([]);
  const [hotelNamesLoading, setHotelNamesLoading] = useState(false);
  const [agentEmailsLoading, setAgentEmailsLoading] = useState(false);
  const isAdmin = isAdminUser();

  // Fetch hotel names and agent emails (for admin) for autocomplete on component mount
  useEffect(() => {
    const fetchAutocompleteData = async () => {
      try {
        const agentId = JSON.parse(localStorage.getItem("UserInfo"))?.id;
        if (agentId) {
          // Fetch hotel names
          setHotelNamesLoading(true);
          const hotelResponse = await getDynamicHotelNames(agentId);
          const hotelNamesData = hotelResponse?.data || [];
          const formattedHotelNames = hotelNamesData.map((name) => ({
            value: name,
            label: name,
          }));
          setHotelNames(formattedHotelNames);
          setHotelNamesLoading(false);

          // Fetch agent emails (only for admin users)
          if (isAdmin) {
            setAgentEmailsLoading(true);
            const agentEmailResponse = await getDynamicAgentEmails();
            const agentEmailsData = agentEmailResponse?.data || [];
            const formattedAgentEmails = agentEmailsData.map((email) => ({
              value: email,
              label: email,
            }));
            setAgentEmails(formattedAgentEmails);
            setAgentEmailsLoading(false);
          }
        }
      } catch (error) {
        console.error("Error fetching autocomplete data:", error);
        setHotelNames([]);
        setAgentEmails([]);
        setHotelNamesLoading(false);
        setAgentEmailsLoading(false);
      }
    };

    fetchAutocompleteData();
  }, [isAdmin]);

  const clearFilters = useCallback(() => {
    setFilters(filterInitialState);
    onFilterChange({});
  }, [onFilterChange]);

  const onFilterFieldChange = useCallback((key, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  }, []);

  const convertFiltersToServerFormat = useCallback(
    (currentFilters) => {
      console.log("Converting filters to server format:", currentFilters); // Debug log
      const serverFilters = {};

      // Add basic string filters
      if (currentFilters.hotel_name?.trim()) {
        serverFilters.hotel_name = currentFilters.hotel_name.trim();
      }
      // Only include agent_email filter for admin users
      if (isAdmin && currentFilters.agent_email?.trim()) {
        serverFilters.agent_email = currentFilters.agent_email.trim();
      }
      if (currentFilters.order_id?.trim()) {
        serverFilters.order_id = currentFilters.order_id.trim();
      }
      if (currentFilters.status?.trim()) {
        serverFilters.status = currentFilters.status.trim();
      }

      // Add date filters with better validation
      if (
        currentFilters.order_dates?.start &&
        currentFilters.order_dates.start !== ""
      ) {
        serverFilters.order_date_start = currentFilters.order_dates.start;
        console.log(
          "Added order_date_start:",
          currentFilters.order_dates.start
        ); // Debug log
      }
      if (
        currentFilters.order_dates?.end &&
        currentFilters.order_dates.end !== ""
      ) {
        serverFilters.order_date_end = currentFilters.order_dates.end;
        console.log("Added order_date_end:", currentFilters.order_dates.end); // Debug log
      }
      if (
        currentFilters.staying_dates?.start &&
        currentFilters.staying_dates.start !== ""
      ) {
        serverFilters.staying_date_start = currentFilters.staying_dates.start;
        console.log(
          "Added staying_date_start:",
          currentFilters.staying_dates.start
        ); // Debug log
      }
      if (
        currentFilters.staying_dates?.end &&
        currentFilters.staying_dates.end !== ""
      ) {
        serverFilters.staying_date_end = currentFilters.staying_dates.end;
        console.log(
          "Added staying_date_end:",
          currentFilters.staying_dates.end
        ); // Debug log
      }

      // Add price filters
      if (
        currentFilters.min_price &&
        !isNaN(parseFloat(currentFilters.min_price))
      ) {
        serverFilters.min_price = parseFloat(currentFilters.min_price);
      }
      if (
        currentFilters.max_price &&
        !isNaN(parseFloat(currentFilters.max_price))
      ) {
        serverFilters.max_price = parseFloat(currentFilters.max_price);
      }

      // Add boolean filters from dropdowns (paid)
      if (currentFilters.paid !== null && currentFilters.paid !== "") {
        serverFilters.paid = currentFilters.paid === "true";
      }

      // Add boolean filters from checkboxes (these are already booleans)
      if (currentFilters.pending === true) {
        serverFilters.pending = true;
      }
      if (currentFilters.cancelled === true) {
        serverFilters.cancelled = true;
      }
      if (currentFilters.flight_booked === true) {
        serverFilters.flight_booked = true;
      }
      if (currentFilters.hotel_booked === true) {
        serverFilters.hotel_booked = true;
      }

      console.log("Final server filters:", serverFilters); // Debug log
      return serverFilters;
    },
    [isAdmin]
  );

  const applyFilters = useCallback(() => {
    console.log("Current filters state:", filters); // Debug log
    const serverFilters = convertFiltersToServerFormat(filters);
    console.log("Server filters being sent:", serverFilters); // Debug log
    onFilterChange(serverFilters);
  }, [filters, onFilterChange, convertFiltersToServerFormat]);

  const renderDatesFilter = (key, title, format = "DD/MM/YYYY") => {
    return (
      <div className={"filter-input-wrapper"}>
        <div>{title}</div>
        <DatePicker.RangePicker
          onChange={(dates) =>
            onFilterFieldChange(key, {
              start: dates && dates[0] ? dates[0].format(format) : "",
              end: dates && dates[1] ? dates[1].format(format) : "",
            })
          }
          format={format}
          locale={dateHebLocale}
        />
      </div>
    );
  };

  const statusOptions = [
    { value: "", name: "כל הסטטוסים" },
    { value: "confirmed", name: "מאושר" },
    { value: "pending", name: "ממתין לאישור" },
    { value: "cancelled", name: "בוטל" },
  ];

  const paymentStatusOptions = [
    { value: "", name: "כל המצבים" },
    { value: "true", name: "שולם" },
    { value: "false", name: "לא שולם" },
  ];

  const bookingStatusOptions = [
    { value: "", name: "כל המצבים" },
    { value: "true", name: "מוזמן" },
    { value: "false", name: "לא מוזמן" },
  ];

  return (
    <div className="dynamic-filters-container">
      <div className="flights-filters-row">
        {renderDatesFilter("order_dates", "תאריך הזמנה")}
        {renderDatesFilter("staying_dates", "תאריכי שהייה")}
      </div>

      <div className="flights-filters-separator" />

      <div className="flights-filters-row">
        <div className="filter-input-wrapper">
          <div>שם מלון</div>
          <AutoComplete
            options={hotelNames}
            value={filters.hotel_name}
            placeholder="הכנס שם מלון"
            onChange={(value) => onFilterFieldChange("hotel_name", value)}
            onSelect={(value) => onFilterFieldChange("hotel_name", value)}
            filterOption={(inputValue, option) =>
              option.value.toLowerCase().includes(inputValue.toLowerCase())
            }
            style={{ width: "100%" }}
            loading={hotelNamesLoading}
          />
        </div>

        {isAdmin && (
          <div className="filter-input-wrapper">
            <div>אימייל סוכן</div>
            <AutoComplete
              options={agentEmails}
              value={filters.agent_email}
              placeholder="הכנס אימייל סוכן"
              onChange={(value) => onFilterFieldChange("agent_email", value)}
              onSelect={(value) => onFilterFieldChange("agent_email", value)}
              filterOption={(inputValue, option) =>
                option.value.toLowerCase().includes(inputValue.toLowerCase())
              }
              style={{ width: "100%" }}
              loading={agentEmailsLoading}
            />
          </div>
        )}

        <StringInput
          title="מספר הזמנה"
          value={filters.order_id}
          onValueChange={(value) => onFilterFieldChange("order_id", value)}
          placeholder="הכנס מספר הזמנה"
        />
      </div>

      <div className="flights-filters-separator" />

      <div className="flights-filters-row">
        <div className="filter-input-wrapper">
          <div>מחיר מינימום</div>
          <StringInput
            value={filters.min_price}
            onValueChange={(value) => onFilterFieldChange("min_price", value)}
            placeholder="מחיר מינימום"
            type="number"
          />
        </div>

        <div className="filter-input-wrapper">
          <div>מחיר מקסימום</div>
          <StringInput
            value={filters.max_price}
            onValueChange={(value) => onFilterFieldChange("max_price", value)}
            placeholder="מחיר מקסימום"
            type="number"
          />
        </div>

        <div className="filter-input-wrapper">
          <div>סטטוס הזמנה</div>
          <Select
            options={statusOptions}
            value={filters.status || ""}
            onChange={(value) => onFilterFieldChange("status", value)}
            placeholder="בחר סטטוס"
          />
        </div>

        <div className="filter-input-wrapper">
          <div>סטטוס תשלום</div>
          <Select
            options={paymentStatusOptions}
            value={filters.paid || ""}
            onChange={(value) => onFilterFieldChange("paid", value)}
            placeholder="בחר סטטוס תשלום"
          />
        </div>
      </div>

      <div className="flights-filters-separator" />

      <div className="flights-filters-row">
        <div className="filter-checkbox-wrapper">
          <CheckboxInput
            title="טיסה מוזמנת"
            value={filters.flight_booked}
            onValueChange={(value) =>
              onFilterFieldChange("flight_booked", value)
            }
          />
        </div>

        <div className="filter-checkbox-wrapper">
          <CheckboxInput
            title="מלון מוזמן"
            value={filters.hotel_booked}
            onValueChange={(value) =>
              onFilterFieldChange("hotel_booked", value)
            }
          />
        </div>

        <Button
          onClick={applyFilters}
          disabled={isLoading}
          style={{ alignSelf: "flex-end" }}
        >
          {isLoading ? "מחפש..." : "חפש"}
        </Button>

        <div className={"flights-filters-clear"} onClick={clearFilters}>
          <InputX onClick={clearFilters} />
          <div>{"איפוס סינונים"}</div>
        </div>
      </div>
    </div>
  );
};

export default DynamicFilters;
